// ============================================================
//  SPOTLIGHTS PAGE — ADMIN DASHBOARD
//  Path: src/pages/SpotlightsPage.tsx
//
//  Rule-based automatic expert featuring. Admin sets the rule;
//  the scheduled job picks the expert automatically based on
//  objective performance criteria. No manual expert selection
//  = no bias.
//
//  NOT active yet — built now, activated later.
//  When ready: enable the spotlight in internalJobs to run
//  the rule-based selection job on a weekly cadence.
// ============================================================

import React, { useEffect, useState } from 'react'
import {
  listSpotlights, createSpotlight, updateSpotlight, deleteSpotlight,
  listAllCategoriesAdmin,
} from '../services/admin.service'
import { useAuth } from '../context/AuthContext'

// FIX (review pass):
//  - customer_service can GET /spotlights but not create / update /
//    delete (admin.routes.js: super_admin, admin only). They saw every
//    button and every click failed. Now: View Only badge, no buttons —
//    same pattern as AdvertisementsPage.tsx.
//  - A failed load no longer ALSO says "No spotlight rules yet. Create
//    one to get started" under the error; it offers Try again.
//  - Enable/Disable and Delete show the server's reason when they fail,
//    and are disabled while their request is in flight (no double toggle).

interface Category { id: string; name: string; group: string | null }

interface Spotlight {
  id:          string
  rule:        string
  isActive:    boolean
  refreshedAt: string | null
  createdAt:   string
  category:    { id: string; name: string; group: string | null } | null
  expert:      { id: string; user: { firstName: string | null; lastName: string | null } } | null
}

const RULES = [
  { value: 'top_rated',        label: '⭐ Top Rated',        desc: 'Highest average rating in the past 30 days' },
  { value: 'most_booked',      label: '📅 Most Booked',      desc: 'Most completed tasks in the past 30 days' },
  { value: 'newest_approved',  label: '🆕 Newest Approved',  desc: 'Most recently approved expert in the category' },
  { value: 'most_responsive',  label: '⚡ Most Responsive',  desc: 'Fastest average time to accept a task' },
]

const EMPTY_FORM = { rule: 'top_rated', categoryId: '', isActive: false }

// Module-level (not inside the component) so load() only references
// module-level values and state setters — keeps react-hooks/exhaustive-deps
// quiet, which matters because a CI=true build treats its warnings as errors.
const serverMessage = (e: any, fallback: string): string =>
  e?.response?.data?.message || (e?.isNetworkError
    ? 'Could not reach the server — check your connection and try again.'
    : fallback)

const SpotlightsPage: React.FC = () => {
  const { user } = useAuth()
  const canModify = !!(user?.roles.includes('super_admin') || user?.roles.includes('admin'))

  const [spotlights, setSpotlights] = useState<Spotlight[]>([])
  const [categories, setCategories] = useState<Category[]>([])
  const [loading, setLoading]       = useState(true)
  const [showForm, setShowForm]     = useState(false)
  const [form, setForm]             = useState({ ...EMPTY_FORM })
  const [saving, setSaving]         = useState(false)
  const [error, setError]           = useState('')
  const [loadFailed, setLoadFailed] = useState(false)
  const [actingId, setActingId]     = useState<string | null>(null)

  const load = async () => {
    setLoading(true)
    setLoadFailed(false)
    try {
      const [sRes, cRes]: any[] = await Promise.all([listSpotlights(), listAllCategoriesAdmin()])
      setSpotlights(sRes?.data?.data ?? [])
      setCategories(cRes?.data?.data ?? [])
    } catch (e: any) {
      console.error('Failed to load spotlights:', e)
      setLoadFailed(true)
      setError(serverMessage(e, 'Failed to load spotlights'))
    }
    finally { setLoading(false) }
  }

  useEffect(() => { load() }, [])

  const handleSubmit = async () => {
    if (!canModify) return
    setSaving(true)
    setError('')
    try {
      await createSpotlight({
        rule:       form.rule as any,
        categoryId: form.categoryId || null,
        isActive:   form.isActive,
      })
      setShowForm(false)
      setForm({ ...EMPTY_FORM })
      await load()
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Save failed')
    } finally { setSaving(false) }
  }

  const handleToggle = async (s: Spotlight) => {
    if (!canModify) return
    setError('')
    setActingId(s.id)
    try { await updateSpotlight(s.id, { isActive: !s.isActive }); await load() }
    catch (e: any) { setError(serverMessage(e, `Failed to ${s.isActive ? 'disable' : 'enable'} this spotlight`)) }
    finally { setActingId(null) }
  }

  const handleDelete = async (id: string) => {
    if (!canModify) return
    if (!window.confirm('Delete this spotlight rule?')) return
    setError('')
    setActingId(id)
    try { await deleteSpotlight(id); await load() }
    catch (e: any) { setError(serverMessage(e, 'Failed to delete this spotlight')) }
    finally { setActingId(null) }
  }

  const ruleInfo = (rule: string) => RULES.find(r => r.value === rule)

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Spotlights</h1>
          <p className="text-gray-400 text-sm mt-1">
            Rule-based automatic featuring — the system picks the expert, not you
          </p>
        </div>
        {canModify ? (
          <button onClick={() => { setShowForm(true); setForm({ ...EMPTY_FORM }) }}
            className="px-4 py-2.5 bg-brand-600 hover:bg-brand-500 text-white rounded-xl text-sm font-medium transition-colors">
            + New Spotlight Rule
          </button>
        ) : (
          <span className="px-3 py-1.5 text-xs rounded-full bg-yellow-600/20 text-yellow-400">View Only</span>
        )}
      </div>

      {/* Info banner */}
      <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-2xl p-4 mb-6">
        <p className="text-sm text-yellow-400 font-medium mb-1">⭐ How Spotlights work</p>
        <p className="text-xs text-yellow-300/70">
          Admin sets a rule (e.g. "Top Rated in Food Delivery"). The scheduled job automatically
          selects the best-matching expert each week and updates the spotlight. No manual
          expert picking = no bias or favoritism. Spotlights are not active yet — enable them
          when you're ready to launch the feature.
        </p>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-4 text-red-400 text-sm flex items-center justify-between gap-3">
          {error}
          {loadFailed ? (
            <button onClick={() => { setError(''); load() }}
              className="px-3 py-1.5 text-xs bg-surface-tertiary text-gray-300 rounded-lg hover:bg-border-light transition-colors shrink-0">
              Try again
            </button>
          ) : (
            <button onClick={() => setError('')} className="text-red-300 hover:text-white ml-3">✕</button>
          )}
        </div>
      )}

      {/* Create form */}
      {showForm && canModify && (
        <div className="bg-surface-card border border-border-light rounded-2xl p-6 mb-6">
          <h2 className="text-lg font-semibold text-white mb-5">New Spotlight Rule</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            {/* Rule */}
            <div className="md:col-span-2">
              <label className="text-xs text-gray-400 mb-2 block">Rule *</label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {RULES.map(r => (
                  <button key={r.value} onClick={() => setForm(f => ({ ...f, rule: r.value }))}
                    className={`p-3 rounded-xl border text-left transition-all ${
                      form.rule === r.value
                        ? 'border-brand-500 bg-brand-500/10'
                        : 'border-border-light bg-surface-tertiary hover:border-gray-500'
                    }`}
                  >
                    <p className="text-sm font-medium text-white">{r.label}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{r.desc}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Category */}
            <div>
              <label className="text-xs text-gray-400 mb-1 block">Category (optional)</label>
              <p className="text-xs text-gray-600 mb-2">Scope to one category, or leave blank for platform-wide</p>
              <select value={form.categoryId}
                onChange={e => setForm(f => ({ ...f, categoryId: e.target.value }))}
                className="w-full bg-surface-tertiary border border-border-light rounded-xl px-4 py-2.5 text-sm text-white focus:border-brand-500 focus:outline-none"
              >
                <option value="">Platform-wide (all categories)</option>
                {categories.map((c: Category) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>

            {/* Active */}
            <div className="flex items-center gap-3 mt-6">
              <input type="checkbox" id="spotActive" checked={form.isActive}
                onChange={e => setForm(f => ({ ...f, isActive: e.target.checked }))}
                className="w-4 h-4"
              />
              <label htmlFor="spotActive" className="text-sm text-gray-300 cursor-pointer">
                Active — enable this spotlight rule
              </label>
            </div>
          </div>

          <div className="flex gap-3">
            <button onClick={handleSubmit} disabled={saving}
              className="px-5 py-2.5 bg-brand-600 hover:bg-brand-500 disabled:opacity-50 text-white rounded-xl text-sm font-medium transition-colors">
              {saving ? 'Creating...' : 'Create Spotlight Rule'}
            </button>
            <button onClick={() => setShowForm(false)}
              className="px-5 py-2.5 bg-surface-tertiary text-gray-300 hover:text-white rounded-xl text-sm transition-colors">
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Spotlights list */}
      {loading ? (
        <div className="bg-surface-card border border-border-light rounded-2xl p-12 text-center text-gray-500">Loading...</div>
      ) : loadFailed ? null : spotlights.length === 0 ? (
        <div className="bg-surface-card border border-border-light rounded-2xl p-12 text-center text-gray-500">
          {canModify ? 'No spotlight rules yet. Create one to get started.' : 'No spotlight rules yet.'}
        </div>
      ) : (
        <div className="bg-surface-card border border-border-light rounded-2xl overflow-hidden">
          <div className="divide-y divide-border-light">
            {spotlights.map(s => (
              <div key={s.id} className="flex items-center gap-4 px-5 py-4">
                <div className="w-10 h-10 rounded-xl bg-yellow-500/15 flex items-center justify-center text-lg flex-shrink-0">
                  ⭐
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-medium text-white">{ruleInfo(s.rule)?.label ?? s.rule}</p>
                    {s.category && (
                      <span className="px-2 py-0.5 text-xs rounded-full bg-brand-500/20 text-brand-400">
                        {s.category.name}
                      </span>
                    )}
                    {!s.category && (
                      <span className="px-2 py-0.5 text-xs rounded-full bg-surface-tertiary text-gray-500">
                        Platform-wide
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">{ruleInfo(s.rule)?.desc}</p>
                  {s.expert && (
                    <p className="text-xs text-green-400 mt-1">
                      Currently: {s.expert.user?.firstName} {s.expert.user?.lastName}
                      {s.refreshedAt && ` · Updated ${new Date(s.refreshedAt).toLocaleDateString()}`}
                    </p>
                  )}
                  {!s.expert && (
                    <p className="text-xs text-gray-600 mt-1">Not yet picked — job will select on next run</p>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className={`px-2.5 py-1 text-xs rounded-full font-medium ${
                    s.isActive ? 'bg-green-500/20 text-green-400' : 'bg-gray-600/20 text-gray-400'
                  }`}>
                    {s.isActive ? 'Active' : 'Inactive'}
                  </span>
                  {canModify && (
                    <>
                      <button onClick={() => handleToggle(s)} disabled={actingId === s.id}
                        className="px-3 py-1.5 text-xs bg-surface-tertiary text-gray-300 hover:text-white rounded-lg disabled:opacity-50 transition-colors">
                        {actingId === s.id ? 'Saving...' : s.isActive ? 'Disable' : 'Enable'}
                      </button>
                      <button onClick={() => handleDelete(s.id)} disabled={actingId === s.id}
                        className="px-3 py-1.5 text-xs bg-red-500/20 text-red-400 hover:bg-red-500/30 rounded-lg disabled:opacity-50 transition-colors">
                        Delete
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default SpotlightsPage